<template>
  <div class="match-feed">
    <h2 class="match-feed-title">
      <v-icon left class="match-feed-title-icon">feed</v-icon>
      Match feed
    </h2>
    <div ref="feedScroll" class="match-feed-scroll" @scroll="onScroll">
      <div v-if="initialLoading" class="match-feed-loading">
        <v-progress-circular indeterminate color="primary" size="40" />
        <p>Loading matches…</p>
      </div>
      <template v-else>
        <div
          v-for="item in feedItems"
          :key="item.fromCanonical ? item.id : item.athleteId + '_' + item.id"
          class="match-feed-card-wrap"
        >
          <div class="match-feed-card">
            <div class="match-feed-card-header">
              <div class="match-feed-avatars">
                <button
                  type="button"
                  class="match-feed-wrestler"
                  @click="navigateToWrestler(displayOrder(item).winnerId)"
                >
                  <div class="match-feed-avatar-ring">
                    <img
                      :src="feedAvatarSrc(displayOrder(item).winnerImage)"
                      class="match-feed-avatar"
                      alt=""
                    />
                  </div>
                  <span class="match-feed-wrestler-name">{{
                    displayOrder(item).winnerName
                  }}</span>
                  <span
                    v-if="displayOrder(item).winnerSchool"
                    class="match-feed-wrestler-school"
                    >{{ displayOrder(item).winnerSchool }}</span
                  >
                  <div class="match-feed-wrestler-rating">
                    <div
                      class="skill-bar skill-bar--feed skill-bar--linear"
                      :aria-label="
                        'Skill ' +
                        feedSkillLabel(displayOrder(item).winnerSkill)
                      "
                    >
                      <div class="skill-bar-linear-track">
                        <div
                          class="skill-bar-linear-fill"
                          :style="{
                            width: feedSkillBarPercent(item, 'winner') + '%',
                          }"
                        ></div>
                      </div>
                      <span class="skill-bar-label">{{
                        feedSkillLabel(displayOrder(item).winnerSkill)
                      }}</span>
                    </div>
                  </div>
                </button>
                <span class="match-feed-vs">vs</span>
                <button
                  type="button"
                  class="match-feed-wrestler"
                  @click="navigateToWrestler(displayOrder(item).loserId)"
                >
                  <div class="match-feed-avatar-ring">
                    <img
                      :src="feedAvatarSrc(displayOrder(item).loserImage)"
                      class="match-feed-avatar"
                      alt=""
                    />
                  </div>
                  <span class="match-feed-wrestler-name">{{
                    displayOrder(item).loserName
                  }}</span>
                  <span
                    v-if="displayOrder(item).loserSchool"
                    class="match-feed-wrestler-school"
                    >{{ displayOrder(item).loserSchool }}</span
                  >
                  <div class="match-feed-wrestler-rating">
                    <div
                      class="skill-bar skill-bar--feed skill-bar--linear"
                      :aria-label="
                        'Skill ' + feedSkillLabel(displayOrder(item).loserSkill)
                      "
                    >
                      <div class="skill-bar-linear-track">
                        <div
                          class="skill-bar-linear-fill"
                          :style="{
                            width: feedSkillBarPercent(item, 'loser') + '%',
                          }"
                        ></div>
                      </div>
                      <span class="skill-bar-label">{{
                        feedSkillLabel(displayOrder(item).loserSkill)
                      }}</span>
                    </div>
                  </div>
                </button>
              </div>
              <div class="match-feed-meta">
                <span class="match-feed-date">{{
                  formatDate(item.matchDate)
                }}</span>
                <span v-if="item.season" class="match-feed-season">{{
                  item.season
                }}</span>
                <span
                  v-if="
                    item.leagueState || item.leagueLeague || item.leagueDivision
                  "
                  class="match-feed-league-meta"
                >
                  {{ formatLeagueMeta(item) }}
                </span>
              </div>
            </div>
            <router-link
              :to="matchLink(item)"
              class="match-feed-card-body-link"
            >
              <div class="match-feed-card-body">
                <p class="match-feed-result">
                  <strong>{{ displayOrder(item).winnerName }}</strong>
                  <span
                    class="match-feed-result-outcome match-feed-result--win"
                  >
                    Win by {{ item.type || "—" }}
                  </span>
                  <strong>{{ displayOrder(item).loserName }}</strong>
                </p>
                <p class="match-feed-event">
                  <span class="match-feed-event-label">Event:</span>
                  {{
                    item.location && item.location.trim() ? item.location : "—"
                  }}
                </p>
                <p v-if="hasSkillDeltas(item)" class="match-feed-skill-deltas">
                  <span class="match-feed-skill-deltas-label"
                    >Skill change</span
                  >
                  <span class="match-feed-skill-deltas-values">
                    <template v-if="skillDeltaShown(item, 'winner')">
                      <span
                        class="match-feed-skill-delta"
                        :class="skillDeltaClass(skillDeltaFor(item, 'winner'))"
                      >
                        {{ displayOrder(item).winnerName }}
                        {{
                          formatSkillDeltaText(skillDeltaFor(item, "winner"))
                        }}
                        skill
                      </span>
                    </template>
                    <span
                      v-if="skillDeltaPairSep(item)"
                      class="match-feed-skill-delta-sep"
                      aria-hidden="true"
                      >·</span
                    >
                    <template v-if="skillDeltaShown(item, 'loser')">
                      <span
                        class="match-feed-skill-delta"
                        :class="skillDeltaClass(skillDeltaFor(item, 'loser'))"
                      >
                        {{ displayOrder(item).loserName }}
                        {{ formatSkillDeltaText(skillDeltaFor(item, "loser")) }}
                        skill
                      </span>
                    </template>
                  </span>
                </p>
                <p v-if="hasMatchVideo(item)" class="match-feed-video">
                  <a
                    :href="normalizeVideoUrl(item.videoLink)"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="match-feed-video-link"
                    @click.stop
                  >
                    <v-icon small class="match-feed-video-icon"
                      >videocam</v-icon
                    >
                    Watch video
                  </a>
                </p>
              </div>
            </router-link>
          </div>
        </div>
        <div v-if="feedLoadFailed" class="match-feed-empty match-feed-error">
          <v-icon size="48" color="grey lighten-1">cloud_off</v-icon>
          <p>Match feed unavailable.</p>
          <p class="match-feed-empty-hint">
            Check the browser console for a missing index link, then add the
            index in Firebase Console (Firestore → Indexes).
          </p>
        </div>
        <div
          v-else-if="feedItems.length === 0 && !initialLoading"
          class="match-feed-empty"
        >
          <v-icon size="48" color="grey lighten-1">sports_mma</v-icon>
          <p>No matches yet.</p>
          <p class="match-feed-empty-hint">
            Matches will appear here when they’re added.
          </p>
        </div>
        <div ref="sentinel" class="match-feed-sentinel" />
        <div v-if="loadingMore" class="match-feed-load-more">
          <v-progress-circular indeterminate color="primary" size="28" />
        </div>
        <div
          v-else-if="hasMore && feedItems.length > 0"
          class="match-feed-load-more-btn-wrap"
        >
          <v-btn
            color="primary"
            outline
            class="match-feed-load-more-btn"
            @click="loadPage"
          >
            Load more matches
          </v-btn>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import {
  getFirestore,
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  doc,
  getDoc,
  startAfter,
  where,
} from "firebase/firestore";
import { MATCHES_CANONICAL_COLLECTION } from "../utils/canonicalMatches";
import {
  seasonFilterState,
  isAllSeasons,
  normalizeSeasonToShort,
} from "../utils/seasonFilter";
import {
  fetchGlobalMaxSkill,
  computeEffectiveSkillBarMax,
  skillBarLinearFillPercent as linearSkillBarFillPercent,
  SKILL_BAR_MIN,
  SKILL_BAR_FALLBACK_MAX,
} from "../utils/skillBarLinear";
import {
  formatSkillLevel,
  formatSkillChangeInline,
} from "../utils/formatSkillDisplay";
import { authState } from "../utils/authState";

const MATCHES_PER_PAGE = 10;
const DOCS_PER_PAGE = 12;
const FALLBACK_ATHLETES_LIMIT = 40;
const FALLBACK_MATCHES_PER_ATHLETE = 20;

export default {
  name: "MatchFeed",
  data() {
    return {
      defaultProfileImg: require("../assets/default-profile.png"),
      feedItems: [],
      initialLoading: true,
      loadingMore: false,
      lastDoc: null,
      hasMore: true,
      athleteNames: {},
      athleteImages: {},
      athleteLeagues: {},
      leaguesByName: {},
      feedLoadFailed: false,
      sentinelObserver: null,
      fallbackMatches: null,
      athleteSkills: {},
      athleteSchools: {},
      feedGlobalSkillMax: null,
      /** Dedupe Firestore reads for opponent-side SkillChange */
      opponentSkillMirrorCache: {},
    };
  },
  computed: {
    authenticated() {
      return authState.user != null;
    },
  },
  mounted() {
    this.loadFirstPage();
    this._seasonUnwatch = this.$watch(
      () => seasonFilterState.selected,
      () => {
        this.loadFirstPage();
      }
    );
  },
  beforeDestroy() {
    if (this._seasonUnwatch) {
      this._seasonUnwatch();
      this._seasonUnwatch = null;
    }
    if (this.sentinelObserver && this.$refs.sentinel) {
      this.sentinelObserver.unobserve(this.$refs.sentinel);
    }
    this.sentinelObserver = null;
  },
  methods: {
    feedAvatarSrc(url) {
      if (this.authenticated && url && String(url).trim())
        return String(url).trim();
      return this.defaultProfileImg;
    },
    matchLink(item) {
      if (item.fromCanonical) {
        const viewer = item.winnerId || item.loserId;
        if (viewer) {
          return `/wrestler/${viewer}/match/${item.id}`;
        }
      }
      return `/wrestler/${item.athleteId}/match/${item.id}`;
    },
    navigateToWrestler(wrestlerId) {
      if (wrestlerId) {
        this.$router.push(`/wrestler/${wrestlerId}`);
      }
    },
    normalizedResult(item) {
      const r = String(item.result || "")
        .trim()
        .toLowerCase();
      if (r === "win") return "Win";
      if (r === "loss") return "Loss";
      return item.result || "—";
    },
    resultClass(result) {
      const r = String(result || "")
        .trim()
        .toLowerCase();
      if (r === "win") return "match-feed-result--win";
      if (r === "loss") return "match-feed-result--loss";
      return "";
    },
    displayOrder(item) {
      if (item.fromCanonical) {
        return {
          winnerName: item.winnerName || "—",
          winnerId: item.winnerId,
          winnerImage: item.winnerImage || "",
          winnerSkill: this.normalizeSkillNumber(item.winnerSkill),
          winnerSchool: this.trimSchool(item.winnerSchool),
          loserName: item.loserName || "—",
          loserId: item.loserId,
          loserImage: item.loserImage || "",
          loserSkill: this.normalizeSkillNumber(item.loserSkill),
          loserSchool: this.trimSchool(item.loserSchool),
        };
      }
      const r = this.normalizedResult(item);
      const athleteWon = r === "Win";
      const wSkill = athleteWon ? item.wrestlerSkill : item.opponentSkill;
      const lSkill = athleteWon ? item.opponentSkill : item.wrestlerSkill;
      return {
        winnerName: athleteWon
          ? item.wrestlerName || "Wrestler"
          : item.opponentName || "Opponent",
        winnerId: athleteWon ? item.athleteId : item.opponentId,
        winnerImage: athleteWon
          ? item.wrestlerImage || ""
          : item.opponentImage || "",
        winnerSkill: this.normalizeSkillNumber(wSkill),
        winnerSchool: athleteWon
          ? this.trimSchool(item.wrestlerSchool)
          : this.trimSchool(item.opponentSchool),
        loserName: athleteWon
          ? item.opponentName || "Opponent"
          : item.wrestlerName || "Wrestler",
        loserId: athleteWon ? item.opponentId : item.athleteId,
        loserImage: athleteWon
          ? item.opponentImage || ""
          : item.wrestlerImage || "",
        loserSkill: this.normalizeSkillNumber(lSkill),
        loserSchool: athleteWon
          ? this.trimSchool(item.opponentSchool)
          : this.trimSchool(item.wrestlerSchool),
      };
    },
    trimSchool(v) {
      if (v == null || v === "") return "";
      const s = String(v).trim();
      return s || "";
    },
    normalizeSkillNumber(raw) {
      if (raw == null || raw === "") return null;
      const n = typeof raw === "number" ? raw : Number(raw);
      return Number.isFinite(n) ? n : null;
    },
    feedBarHigh(item) {
      const o = this.displayOrder(item);
      const nums = [o.winnerSkill, o.loserSkill].filter(
        (n) => n != null && Number.isFinite(n)
      );
      return computeEffectiveSkillBarMax(this.feedGlobalSkillMax, ...nums);
    },
    feedSkillBarPercent(item, role) {
      const o = this.displayOrder(item);
      const skill = role === "winner" ? o.winnerSkill : o.loserSkill;
      const s = skill != null && Number.isFinite(skill) ? skill : SKILL_BAR_MIN;
      const high = this.feedBarHigh(item);
      return linearSkillBarFillPercent(s, high, SKILL_BAR_MIN);
    },
    feedSkillLabel(skill) {
      if (skill == null || !Number.isFinite(skill)) return "—";
      return formatSkillLevel(skill);
    },
    hasMatchVideo(item) {
      if (!item) return false;
      const url = item.videoLink || item.video;
      return url != null && String(url).trim() !== "";
    },
    normalizeVideoUrl(url) {
      const s = String(url || "").trim();
      if (!s) return "#";
      if (!/^https?:\/\//i.test(s)) return "https://" + s;
      return s;
    },
    formatDate(matchDate) {
      if (!matchDate) return "—";
      try {
        const d =
          typeof matchDate.toDate === "function"
            ? matchDate.toDate()
            : matchDate instanceof Date
            ? matchDate
            : new Date(matchDate);
        if (isNaN(d.getTime())) return "—";
        return this.$moment(d).format("MMM D, YYYY");
      } catch (_) {
        return "—";
      }
    },
    formatLeagueMeta(item) {
      const parts = [
        item.leagueState,
        item.leagueLeague,
        item.leagueDivision,
      ].filter(Boolean);
      return parts.length ? parts.join(" · ") : "";
    },
    athleteIdFromPath(path) {
      if (!path) return null;
      const parts = path.split("/");
      const idx = parts.indexOf("athletes");
      if (idx >= 0 && parts[idx + 1]) return parts[idx + 1];
      return null;
    },
    async loadAthleteInfo(athleteId) {
      if (this.athleteNames[athleteId] !== undefined) return;
      const db = getFirestore();
      try {
        const snap = await getDoc(doc(db, "athletes", athleteId));
        const exists =
          typeof snap.exists === "function" ? snap.exists() : snap.exists;
        if (exists) {
          const d = snap.data();
          this.$set(
            this.athleteNames,
            athleteId,
            (d.FirstName || "") + " " + (d.LastName || "").trim() || "—"
          );
          this.$set(this.athleteImages, athleteId, d.Image || "");
          this.$set(
            this.athleteLeagues,
            athleteId,
            d.RankLeague != null && String(d.RankLeague).trim() !== ""
              ? String(d.RankLeague).trim()
              : ""
          );
          const sk = d.Skill;
          const sn =
            typeof sk === "number" ? sk : sk != null ? Number(sk) : null;
          this.$set(
            this.athleteSkills,
            athleteId,
            Number.isFinite(sn) ? sn : null
          );
          const school =
            d.School != null && String(d.School).trim() !== ""
              ? String(d.School).trim()
              : "";
          this.$set(this.athleteSchools, athleteId, school);
        } else {
          this.$set(this.athleteNames, athleteId, "—");
          this.$set(this.athleteImages, athleteId, "");
          this.$set(this.athleteLeagues, athleteId, "");
          this.$set(this.athleteSkills, athleteId, null);
          this.$set(this.athleteSchools, athleteId, "");
        }
      } catch (_) {
        this.$set(this.athleteNames, athleteId, "—");
        this.$set(this.athleteImages, athleteId, "");
        this.$set(this.athleteLeagues, athleteId, "");
        this.$set(this.athleteSkills, athleteId, null);
        this.$set(this.athleteSchools, athleteId, "");
      }
    },
    async loadLeaguesMap() {
      if (Object.keys(this.leaguesByName).length > 0) return;
      const db = getFirestore();
      try {
        const snap = await getDocs(collection(db, "leagues"));
        snap.forEach((docSnap) => {
          const d = docSnap.data() || {};
          const name =
            d.Name != null && String(d.Name).trim() !== ""
              ? String(d.Name).trim()
              : null;
          if (name) {
            this.$set(this.leaguesByName, name, {
              State:
                d.State != null && String(d.State).trim() !== ""
                  ? String(d.State).trim()
                  : "",
              League:
                d.League != null && String(d.League).trim() !== ""
                  ? String(d.League).trim()
                  : "",
              Division:
                d.Division != null && String(d.Division).trim() !== ""
                  ? String(d.Division).trim()
                  : "",
            });
          }
        });
      } catch (_) {
        // non-fatal
      }
    },
    async ensureFeedGlobalSkillMax() {
      if (this.feedGlobalSkillMax != null) return;
      try {
        const db = getFirestore();
        this.feedGlobalSkillMax = await fetchGlobalMaxSkill(db);
      } catch (_) {
        this.feedGlobalSkillMax = SKILL_BAR_FALLBACK_MAX;
      }
    },
    async loadFirstPage() {
      this.initialLoading = true;
      this.feedLoadFailed = false;
      this.feedItems = [];
      this.lastDoc = null;
      this.fallbackMatches = null;
      this.hasMore = true;
      try {
        await this.ensureFeedGlobalSkillMax();
        await this.loadPage();
      } catch (_) {
        this.hasMore = false;
        this.feedLoadFailed = true;
      } finally {
        this.initialLoading = false;
        this.$nextTick(() => this.setupSentinelObserver());
      }
    },
    setupSentinelObserver() {
      if (this.sentinelObserver && this.$refs.sentinel) {
        this.sentinelObserver.unobserve(this.$refs.sentinel);
      }
      this.sentinelObserver = null;
      const sentinel = this.$refs.sentinel;
      const scrollEl = this.$refs.feedScroll;
      if (!sentinel || typeof IntersectionObserver === "undefined") return;
      const loadWhenVisible = (entries) => {
        if (
          this.initialLoading ||
          this.loadingMore ||
          !this.hasMore ||
          !entries[0]?.isIntersecting
        )
          return;
        this.loadPage();
      };
      const opts = { rootMargin: "400px", threshold: 0 };
      this.sentinelObserver = new IntersectionObserver(
        loadWhenVisible,
        scrollEl ? { ...opts, root: scrollEl } : { ...opts, root: null }
      );
      this.sentinelObserver.observe(sentinel);
    },
    async loadPage() {
      if (this.loadingMore || !this.hasMore) return;
      this.loadingMore = true;
      if (this.fallbackMatches !== null) {
        const nextStart = this.feedItems.length;
        const nextChunk = this.fallbackMatches.slice(
          nextStart,
          nextStart + MATCHES_PER_PAGE
        );
        this.feedItems.push(...nextChunk);
        this.enrichFeedItems(nextChunk).catch(() => {});
        this.hasMore = this.feedItems.length < this.fallbackMatches.length;
        this.loadingMore = false;
        return;
      }
      const db = getFirestore();
      try {
        const constraints = [];
        if (!isAllSeasons(seasonFilterState.selected)) {
          const norm = normalizeSeasonToShort(
            String(seasonFilterState.selected).trim()
          );
          // Use == (not `in`) so Firestore uses the composite Season + MatchDate index.
          // `in` + orderBy often returns no rows for collection-group queries without a matching index.
          if (norm && norm !== "All") {
            constraints.push(where("Season", "==", norm));
          }
        }
        constraints.push(orderBy("MatchDate", "desc"));
        constraints.push(limit(DOCS_PER_PAGE));
        if (this.lastDoc) constraints.push(startAfter(this.lastDoc));
        const q = query(
          collection(db, MATCHES_CANONICAL_COLLECTION),
          ...constraints
        );
        const snap = await getDocs(q);
        const { items: newItems, lastDoc: nextLastDoc } = this.snapToFeedItems(
          snap,
          MATCHES_PER_PAGE
        );
        if (
          newItems.length === 0 &&
          !this.lastDoc &&
          !this.feedItems.length &&
          !isAllSeasons(seasonFilterState.selected) &&
          this.fallbackMatches === null
        ) {
          await this.loadPageFallback();
          this.loadingMore = false;
          return;
        }
        this.feedItems.push(...newItems);
        this.enrichFeedItems(newItems).catch(() => {});
        if (newItems.length < MATCHES_PER_PAGE) this.hasMore = false;
        else this.lastDoc = nextLastDoc;
      } catch (err) {
        if (!this.feedItems.length && !this.lastDoc) {
          try {
            await this.loadPageFallback();
          } catch (_) {
            this.feedLoadFailed = true;
          }
        } else {
          this.hasMore = false;
          if (!this.feedItems.length) this.feedLoadFailed = true;
        }
      } finally {
        this.loadingMore = false;
      }
    },
    snapToFeedItems(snap, maxItems = 10) {
      const newItems = [];
      const docs = snap.docs || [];
      for (let i = 0; i < docs.length; i++) {
        const d = docs[i];
        const data = d.data() || {};
        const winnerId = data.WinnerAthleteId || "";
        const loserId = data.LoserAthleteId || "";
        newItems.push({
          id: d.id,
          fromCanonical: true,
          winnerId,
          loserId,
          winnerName: null,
          loserName: null,
          winnerImage: "",
          loserImage: "",
          winnerSkill: null,
          loserSkill: null,
          winnerSchool: "",
          loserSchool: "",
          type: data.Type || "",
          matchDate: data.MatchDate,
          season:
            normalizeSeasonToShort(String(data.Season || "").trim()) ||
            String(data.Season || "").trim() ||
            "",
          location: data.Location || "",
          videoLink: data.VideoLink || data.Video || "",
          skillChangeWinner:
            typeof data.SkillChangeWinner === "number"
              ? data.SkillChangeWinner
              : null,
          skillChangeLoser:
            typeof data.SkillChangeLoser === "number"
              ? data.SkillChangeLoser
              : null,
        });
        if (newItems.length >= maxItems) break;
      }
      const items = newItems.slice(0, maxItems);
      const lastDoc = docs.length > 0 ? docs[docs.length - 1] : null;
      return { items, lastDoc };
    },
    async enrichFeedItems(newItems) {
      const ids = new Set();
      newItems.forEach((item) => {
        if (item.fromCanonical) {
          if (item.winnerId) ids.add(item.winnerId);
          if (item.loserId) ids.add(item.loserId);
        } else {
          if (item.athleteId) ids.add(item.athleteId);
          if (item.opponentId) ids.add(item.opponentId);
        }
      });
      await this.loadLeaguesMap();
      await Promise.all([...ids].map((id) => this.loadAthleteInfo(id)));
      newItems.forEach((item) => {
        if (item.fromCanonical) {
          item.winnerName = this.athleteNames[item.winnerId] || "—";
          item.loserName = this.athleteNames[item.loserId] || "—";
          item.winnerImage = this.athleteImages[item.winnerId] || "";
          item.loserImage = this.athleteImages[item.loserId] || "";
          item.winnerSkill = this.athleteSkills[item.winnerId];
          item.loserSkill = this.athleteSkills[item.loserId];
          item.winnerSchool = this.athleteSchools[item.winnerId] || "";
          item.loserSchool = this.athleteSchools[item.loserId] || "";
          const leagueName = this.athleteLeagues[item.winnerId] || "";
          const meta = leagueName ? this.leaguesByName[leagueName] : null;
          if (meta) {
            item.leagueState = meta.State || "";
            item.leagueLeague = meta.League || "";
            item.leagueDivision = meta.Division || "";
          } else {
            item.leagueState = "";
            item.leagueLeague = "";
            item.leagueDivision = "";
          }
          return;
        }
        item.wrestlerName = this.athleteNames[item.athleteId] || "—";
        item.wrestlerImage = this.athleteImages[item.athleteId] || "";
        item.wrestlerSkill = this.athleteSkills[item.athleteId];
        item.wrestlerSchool = this.athleteSchools[item.athleteId] || "";
        if (
          item.opponentId &&
          (!item.opponentImage || item.opponentImage === "")
        ) {
          item.opponentImage = this.athleteImages[item.opponentId] || "";
        }
        if (item.opponentId) {
          item.opponentName =
            this.athleteNames[item.opponentId] || item.opponentName || "—";
          item.opponentSkill = this.athleteSkills[item.opponentId];
          item.opponentSchool = this.athleteSchools[item.opponentId] || "";
        } else {
          item.opponentSchool = "";
        }
        const leagueName = this.athleteLeagues[item.athleteId] || "";
        const meta = leagueName ? this.leaguesByName[leagueName] : null;
        if (meta) {
          item.leagueState = meta.State || "";
          item.leagueLeague = meta.League || "";
          item.leagueDivision = meta.Division || "";
        } else {
          item.leagueState = "";
          item.leagueLeague = "";
          item.leagueDivision = "";
        }
      });
      await Promise.all(
        newItems
          .filter((item) => !item.fromCanonical)
          .map((item) => this.loadOpponentSkillChangeForFeedItem(item))
      );
    },
    mirrorSkillCacheKey(item) {
      if (!item.opponentId || !item.athleteId) return "";
      return [
        item.opponentId,
        item.athleteId,
        this.matchDateToMillis(item.matchDate),
        item.type || "",
      ].join("_");
    },
    async loadOpponentSkillChangeForFeedItem(item) {
      if (!item.opponentId || !item.athleteId || !item.matchDate) return;
      const cacheKey = this.mirrorSkillCacheKey(item);
      if (
        cacheKey &&
        Object.prototype.hasOwnProperty.call(
          this.opponentSkillMirrorCache,
          cacheKey
        )
      ) {
        this.$set(
          item,
          "opponentSkillChange",
          this.opponentSkillMirrorCache[cacheKey]
        );
        return;
      }
      try {
        const db = getFirestore();
        const oppRef = collection(db, "athletes", item.opponentId, "matches");
        const q = query(
          oppRef,
          where("WrestlerID", "==", item.athleteId),
          where("MatchDate", "==", item.matchDate),
          where("Type", "==", item.type || "")
        );
        const snap = await getDocs(q);
        let val = null;
        if (!snap.empty) {
          const sc = snap.docs[0].data().SkillChange;
          const n = typeof sc === "number" ? sc : Number(sc);
          val = Number.isFinite(n) ? n : null;
        }
        if (cacheKey) {
          this.$set(this.opponentSkillMirrorCache, cacheKey, val);
        }
        this.$set(item, "opponentSkillChange", val);
      } catch (_e) {
        this.$set(item, "opponentSkillChange", null);
      }
    },
    skillDeltaFor(item, role) {
      if (!item) return null;
      if (item.fromCanonical) {
        const raw =
          role === "winner" ? item.skillChangeWinner : item.skillChangeLoser;
        if (raw == null) return null;
        const n = typeof raw === "number" ? raw : Number(raw);
        return Number.isFinite(n) ? n : null;
      }
      const o = this.displayOrder(item);
      const wid = role === "winner" ? o.winnerId : o.loserId;
      let raw = null;
      if (wid === item.athleteId) raw = item.skillChange;
      else if (wid === item.opponentId) raw = item.opponentSkillChange;
      if (raw == null) return null;
      const n = typeof raw === "number" ? raw : Number(raw);
      return Number.isFinite(n) ? n : null;
    },
    skillDeltaShown(item, role) {
      const n = this.skillDeltaFor(item, role);
      return n != null && Number.isFinite(n);
    },
    skillDeltaPairSep(item) {
      return (
        this.skillDeltaShown(item, "winner") &&
        this.skillDeltaShown(item, "loser")
      );
    },
    hasSkillDeltas(item) {
      return (
        this.skillDeltaShown(item, "winner") ||
        this.skillDeltaShown(item, "loser")
      );
    },
    formatSkillDeltaText(n) {
      return formatSkillChangeInline(n);
    },
    skillDeltaClass(delta) {
      if (delta == null || !Number.isFinite(delta)) return "";
      if (delta > 0) return "match-feed-skill-delta--gain";
      if (delta < 0) return "match-feed-skill-delta--loss";
      return "match-feed-skill-delta--flat";
    },
    matchDateToMillis(matchDate) {
      if (!matchDate) return 0;
      try {
        const d =
          typeof matchDate.toDate === "function"
            ? matchDate.toDate()
            : matchDate instanceof Date
            ? matchDate
            : new Date(matchDate);
        return d.getTime();
      } catch (_) {
        return 0;
      }
    },
    async loadPageFallback() {
      const db = getFirestore();
      const cap = FALLBACK_ATHLETES_LIMIT * FALLBACK_MATCHES_PER_ATHLETE;
      const canonRef = collection(db, MATCHES_CANONICAL_COLLECTION);
      let allMatches = [];
      try {
        const matchesSnap = await getDocs(
          query(canonRef, orderBy("MatchDate", "desc"), limit(cap))
        );
        matchesSnap.forEach((d) => {
          const data = d.data() || {};
          allMatches.push({
            id: d.id,
            fromCanonical: true,
            winnerId: data.WinnerAthleteId || "",
            loserId: data.LoserAthleteId || "",
            winnerName: null,
            loserName: null,
            winnerImage: "",
            loserImage: "",
            winnerSkill: null,
            loserSkill: null,
            winnerSchool: "",
            loserSchool: "",
            type: data.Type || "",
            matchDate: data.MatchDate,
            season:
              normalizeSeasonToShort(String(data.Season || "").trim()) ||
              String(data.Season || "").trim() ||
              "",
            location: data.Location || "",
            videoLink: data.VideoLink || data.Video || "",
            skillChangeWinner:
              typeof data.SkillChangeWinner === "number"
                ? data.SkillChangeWinner
                : null,
            skillChangeLoser:
              typeof data.SkillChangeLoser === "number"
                ? data.SkillChangeLoser
                : null,
            leagueState: "",
            leagueLeague: "",
            leagueDivision: "",
          });
        });
      } catch (_) {
        allMatches = [];
      }
      allMatches.sort(
        (a, b) =>
          this.matchDateToMillis(b.matchDate) -
          this.matchDateToMillis(a.matchDate)
      );
      let filtered = allMatches;
      if (!isAllSeasons(seasonFilterState.selected)) {
        const selShort = normalizeSeasonToShort(
          String(seasonFilterState.selected).trim()
        );
        filtered = allMatches.filter(
          (m) =>
            normalizeSeasonToShort(String(m.season || "").trim()) === selShort
        );
      }
      this.fallbackMatches = filtered;
      const newItems = filtered.slice(0, MATCHES_PER_PAGE);
      this.feedItems.push(...newItems);
      this.enrichFeedItems(newItems).catch(() => {});
      this.hasMore = filtered.length > MATCHES_PER_PAGE;
    },
    onScroll() {
      const el = this.$refs.feedScroll;
      const sentinel = this.$refs.sentinel;
      if (
        !el ||
        !sentinel ||
        this.initialLoading ||
        this.loadingMore ||
        !this.hasMore
      )
        return;
      const rect = sentinel.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      if (rect.top <= elRect.bottom + 400) this.loadPage();
    },
  },
};
</script>

<style scoped>
.match-feed {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.match-feed-title {
  display: flex;
  align-items: center;
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
  color: #1f2937;
  padding: 0 0.25rem;
}

.match-feed-title-icon {
  color: #b91c1c;
}

.match-feed-scroll {
  overflow-y: auto;
  max-height: 60vh;
  min-height: 200px;
  padding: 2px;
  -webkit-overflow-scrolling: touch;
}

.match-feed-loading,
.match-feed-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  color: #6b7280;
  gap: 0.5rem;
}

.match-feed-empty-hint {
  font-size: 0.875rem;
  margin: 0;
  color: #9ca3af;
}

.match-feed-card-wrap {
  margin-bottom: 1rem;
}

.match-feed-card-wrap:last-of-type {
  margin-bottom: 0.5rem;
}

.match-feed-card {
  display: block;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  text-decoration: none;
  color: inherit;
  transition: box-shadow 0.2s ease, transform 0.15s ease;
  border: 1px solid #e5e7eb;
}

.match-feed-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
  border-color: #d1d5db;
}

.match-feed-card-header {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: flex-start;
  column-gap: 12px;
  padding: 14px 16px;
  background: linear-gradient(135deg, #fef2f2 0%, #fff 100%);
  border-bottom: 1px solid #f3f4f6;
}

.match-feed-avatars {
  grid-column: 2;
  grid-row: 1;
  justify-self: center;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 10px;
  min-width: 0;
}

.match-feed-wrestler {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 4px;
  margin: 0;
  border: none;
  border-radius: 12px;
  background: transparent;
  cursor: pointer;
  font: inherit;
  color: inherit;
  max-width: 140px;
  min-width: 0;
  text-align: center;
  transition: background 0.2s ease;
}

.match-feed-wrestler:hover {
  background: rgba(0, 0, 0, 0.04);
}

.match-feed-wrestler:focus {
  outline: 2px solid #b91c1c;
  outline-offset: 2px;
}

.match-feed-avatar-ring {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}

.match-feed-avatar {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.match-feed-wrestler-name {
  font-size: 0.8rem;
  font-weight: 600;
  line-height: 1.25;
  word-break: break-word;
  max-width: 132px;
}

.match-feed-wrestler-school {
  font-size: 0.72rem;
  font-weight: 500;
  line-height: 1.25;
  text-align: center;
  color: #6b7280;
  max-width: 140px;
  word-break: break-word;
}

.match-feed-wrestler-rating {
  width: 100%;
  max-width: 128px;
}

.skill-bar.skill-bar--feed {
  min-width: 100px;
  max-width: 128px;
  width: 100%;
  position: relative;
}

.skill-bar--feed.skill-bar--linear .skill-bar-linear-track {
  height: 12px;
  border-radius: 999px;
  background: #e5e7eb;
  overflow: hidden;
}

.skill-bar--feed.skill-bar--linear .skill-bar-linear-fill {
  height: 100%;
  border-radius: 999px;
  min-width: 0;
  background: linear-gradient(
    90deg,
    #22c55e 0%,
    #a3e635 22%,
    #facc15 48%,
    #f97316 74%,
    #b91c1c 100%
  );
  transition: width 0.25s ease;
}

.skill-bar--feed .skill-bar-label {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  font-size: 0.65rem;
  font-weight: 600;
  color: #111827;
  pointer-events: none;
}

.match-feed-vs {
  font-size: 0.8rem;
  font-weight: 700;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  flex-shrink: 0;
  margin-top: 18px;
}

.match-feed-meta {
  grid-column: 3;
  grid-row: 1;
  justify-self: end;
  align-self: center;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  flex-shrink: 0;
  min-width: 0;
}

@media (max-width: 400px) {
  .match-feed-card-header {
    grid-template-columns: 1fr;
    row-gap: 10px;
  }

  .match-feed-avatars {
    grid-column: 1;
    grid-row: 1;
    justify-self: center;
  }

  .match-feed-meta {
    grid-column: 1;
    grid-row: 2;
    justify-self: center;
    align-items: center;
    text-align: center;
  }
}

.match-feed-date {
  font-size: 0.8rem;
  font-weight: 600;
  color: #374151;
}

.match-feed-season {
  font-size: 0.75rem;
  color: #6b7280;
}
.match-feed-league-meta {
  display: block;
  font-size: 0.7rem;
  color: #9ca3af;
  margin-top: 2px;
}

.match-feed-card-body-link {
  display: block;
  text-decoration: none;
  color: inherit;
  cursor: pointer;
}

.match-feed-card-body {
  padding: 14px 16px;
}

.match-feed-result {
  margin: 0 0 8px;
  font-size: 0.95rem;
  line-height: 1.45;
  color: #1f2937;
}

.match-feed-result-outcome {
  display: inline-block;
  margin: 0 4px;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
}

.match-feed-result--win {
  background: #dcfce7;
  color: #166534;
}

.match-feed-result--loss {
  background: #fee2e2;
  color: #991b1b;
}

.match-feed-event {
  margin: 0;
  font-size: 0.85rem;
  color: #6b7280;
}

.match-feed-event-label {
  font-weight: 600;
  color: #374151;
  margin-right: 4px;
}

.match-feed-video {
  margin: 10px 0 0;
}

.match-feed-video-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  color: #b91c1c;
  text-decoration: none;
}

.match-feed-video-link:hover {
  text-decoration: underline;
}

.match-feed-video-icon {
  color: #b91c1c !important;
}

.match-feed-skill-deltas {
  margin: 10px 0 0;
  font-size: 0.85rem;
  line-height: 1.45;
  color: #374151;
}

.match-feed-skill-deltas-label {
  display: block;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 4px;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.match-feed-skill-deltas-values {
  display: block;
}

.match-feed-skill-delta {
  font-weight: 600;
}

.match-feed-skill-delta-sep {
  margin: 0 8px;
  color: #9ca3af;
  font-weight: 400;
}

.match-feed-skill-delta--gain {
  color: #15803d;
}

.match-feed-skill-delta--loss {
  color: #b91c1c;
}

.match-feed-skill-delta--flat {
  color: #6b7280;
}

.match-feed-sentinel {
  height: 1px;
  visibility: hidden;
  pointer-events: none;
}

.match-feed-load-more,
.match-feed-load-more-btn-wrap {
  display: flex;
  justify-content: center;
  padding: 16px;
}
.match-feed-load-more-btn {
  text-transform: none;
}

/* Dark mode */
.theme--dark .match-feed-title {
  color: #e5e7eb;
}
.theme--dark .match-feed-card {
  background: #1e1e1e;
  border-color: #374151;
}
.theme--dark .match-feed-card-header {
  background: linear-gradient(135deg, #2d1f1f 0%, #1e1e1e 100%);
  border-bottom-color: #374151;
}
.theme--dark .match-feed-date {
  color: #d1d5db;
}
.theme--dark .match-feed-season {
  color: #9ca3af;
}
.theme--dark .match-feed-league-meta {
  color: #6b7280;
}
.theme--dark .match-feed-result {
  color: #e5e7eb;
}
.theme--dark .match-feed-event-label {
  color: #d1d5db;
}
.theme--dark .match-feed-event {
  color: #9ca3af;
}

.theme--dark .match-feed-video-link,
.theme--dark .match-feed-video-icon {
  color: #f87171 !important;
}

.theme--dark .match-feed-skill-deltas {
  color: #d1d5db;
}

.theme--dark .match-feed-skill-deltas-label {
  color: #e5e7eb;
}

.theme--dark .match-feed-skill-delta--gain {
  color: #86efac;
}

.theme--dark .match-feed-skill-delta--loss {
  color: #fca5a5;
}

.theme--dark .match-feed-skill-delta--flat {
  color: #9ca3af;
}

.theme--dark .match-feed-skill-delta-sep {
  color: #6b7280;
}
.theme--dark .match-feed-result--win {
  background: rgba(34, 197, 94, 0.25);
  color: #86efac;
}
.theme--dark .match-feed-result--loss {
  background: rgba(185, 28, 28, 0.25);
  color: #fca5a5;
}

.theme--dark .match-feed-wrestler:hover {
  background: rgba(255, 255, 255, 0.06);
}

.theme--dark .match-feed-avatar-ring {
  border-color: rgba(255, 255, 255, 0.12);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.35);
}

.theme--dark .skill-bar--feed.skill-bar--linear .skill-bar-linear-track {
  background: #374151;
}

.theme--dark .skill-bar--feed .skill-bar-label {
  color: #f3f4f6;
}

.theme--dark .match-feed-wrestler-school {
  color: #9ca3af;
}
</style>
